import { EllipsisOutlined } from '@ant-design/icons'
import { Button, PageHeader, Popconfirm, Popover, Space, Table } from 'antd'
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { TABLE_MAX_HEIGHT } from '@/constants/customer'
import routes from '@/constants/routes'
import { Detergent, DetergentHistory } from '@/types'
import { formatHm, formatYMD } from '@/utils/date'
import { formatJapaneseNumber, formatPrice } from '@/utils/formatPrice'

import { Container, CustomRowTable } from './styles'

type Props = {
  detergentId: number
  dataSource: DetergentHistory[] | undefined
  dataSourceDetail: Detergent[]
  getDetergentHistoriesLoading: boolean
  onDelete: (id: DetergentHistory['id'], detergentId: number) => void
  page: number
  pageSize: number
  totalItems: number
  onChange: (config: TablePaginationConfig) => void
}

const DetergentHistoriesTable = ({
  detergentId,
  dataSource,
  dataSourceDetail,
  getDetergentHistoriesLoading,
  onDelete,
  page,
  pageSize,
  totalItems,
  onChange,
}: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [visibleConfirmDelete, setVisibleConfirmDelete] = useState<Record<string, boolean>>({})

  const detergentHistoryFirstItem = dataSource?.length && dataSource[0].id

  const detergentColumns: ColumnsType<Detergent> = [
    {
      title: t('components.pages.detergents.list.table.name'),
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      render: (record: string) => {
        return <CustomRowTable type="left">{record}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap-detergent',
    },
    {
      title: t('components.pages.detergents.list.table.price'),
      dataIndex: 'price',
      key: 'price',
      render: (record: number) => {
        return <CustomRowTable type="right">{formatJapaneseNumber(formatPrice(record))}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap-detergent',
    },
    {
      title: t('components.pages.detergents.list.table.netWeightG'),
      dataIndex: 'netWeightG',
      key: 'netWeightG',
      render: (record: number) => {
        return <CustomRowTable type="right">{formatJapaneseNumber(record)}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap-detergent',
    },
    {
      title: t('components.pages.detergents.list.table.netWeightMl'),
      dataIndex: 'netWeightMl',
      key: 'netWeightMl',
      render: (record: number) => {
        return <CustomRowTable type="right">{formatJapaneseNumber(record)}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap-detergent',
    },
    {
      title: t('components.pages.detergents.list.table.specificGravity'),
      dataIndex: 'specificGravity',
      key: 'specificGravity',
      render: (record: number) => {
        return <CustomRowTable type="right">{formatJapaneseNumber(record)}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap-detergent',
    },
    {
      title: t('components.pages.detergents.list.table.costPerMl'),
      dataIndex: 'costPerMl',
      key: 'costPerMl',
      render: (record: number) => {
        return <CustomRowTable type="right">{formatPrice(record)}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap-detergent',
    },
    {
      title: t('components.pages.detergents.list.table.remainingQuantity'),
      dataIndex: 'remainingQuantity',
      key: 'remainingQuantity',
      render: (record: number) => {
        return <CustomRowTable type="right">{formatJapaneseNumber(record)}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap-detergent',
    },
    {
      title: t('components.pages.detergents.list.table.thresholdQuantity'),
      dataIndex: 'thresholdQuantity',
      key: 'thresholdQuantity',
      render: (record: number) => {
        return <CustomRowTable type="right">{formatJapaneseNumber(record)}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap-detergent',
    },
  ]

  const columns: ColumnsType<DetergentHistory> = [
    {
      title: t('components.pages.detergentHistories.list.table.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      render: (record: number) => {
        return <CustomRowTable type="right">{formatJapaneseNumber(record)}</CustomRowTable>
      },
      align: 'center' as const,
    },
    {
      title: t('components.pages.detergentHistories.list.table.price'),
      dataIndex: 'price',
      key: 'price',
      render: (record: number) => {
        return <CustomRowTable type="right">{formatJapaneseNumber(formatPrice(record))}</CustomRowTable>
      },
      align: 'center' as const,
    },
    {
      title: t('components.pages.detergentHistories.list.table.updatedAt'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (record: string) => {
        return (
          <CustomRowTable type="right">
            {formatYMD(record)} {formatHm(record)}
          </CustomRowTable>
        )
      },
      align: 'center' as const,
    },
  ]

  columns.push({
    align: 'center' as const,
    render: (text: string, record: DetergentHistory) => {
      return (
        detergentHistoryFirstItem === record.id && (
          <Space size="middle">
            <Popover
              placement="bottomRight"
              content={
                <Space size="small" direction="vertical">
                  <Button size="small" onClick={() => gotoEditDetergentHistoryPage(record.id, detergentId)}>
                    {t('components.pages.admins.list.table.update')}
                  </Button>
                  <Popconfirm
                    id={record.id.toString()}
                    visible={!!visibleConfirmDelete[record.id]}
                    title={t('components.pages.attendanceLogs.list.table.confirmDelete')}
                    okText={t('common.yes')}
                    cancelText={t('common.no')}
                    onConfirm={() => {
                      onDelete(record.id, detergentId)
                    }}
                    onCancel={() => setVisibleConfirmDelete({})}
                  >
                    <Button
                      size="small"
                      danger
                      onClick={() => setVisibleConfirmDelete({ [record.id]: true })}
                      loading={false}
                    >
                      {t('components.pages.admins.list.table.delete')}
                    </Button>
                  </Popconfirm>
                </Space>
              }
            >
              <Button>
                <EllipsisOutlined />
              </Button>
            </Popover>
          </Space>
        )
      )
    },
  })

  const gotoEditDetergentHistoryPage = (id: Detergent['id'], detergentId: number) => {
    const path = routes.updateDetergentHistory.path
      .replace(':detergentId', detergentId.toString())
      .replace(':id', id.toString())
    navigate(path)
  }

  return (
    <Container>
      <Table
        rowKey="id"
        bordered
        dataSource={dataSourceDetail}
        columns={detergentColumns}
        pagination={false}
        scroll={{ x: 'max-content', y: TABLE_MAX_HEIGHT }}
      />
      <div>
        <PageHeader
          ghost={false}
          title={`${t('components.pages.detergentHistories.list.pageHeader.title')}`}
          extra={
            <Button
              key="action"
              type="primary"
              onClick={() => {
                const path = routes.createDetergentHistory.path.replace(':id', detergentId.toString())
                navigate(path)
              }}
            >
              {t('components.pages.detergentHistories.list.pageHeader.new')}
            </Button>
          }
        />
        <Table
          rowKey="id"
          bordered
          dataSource={dataSource}
          columns={columns}
          loading={getDetergentHistoriesLoading}
          pagination={{
            pageSize,
            current: page,
            total: totalItems,
            showSizeChanger: false,
          }}
          onChange={onChange}
          scroll={{ y: TABLE_MAX_HEIGHT }}
        />
      </div>
    </Container>
  )
}

DetergentHistoriesTable.displayName = 'DetergentHistoriesTable'
export default DetergentHistoriesTable
